/**
 * @fileoverview useTransactionCategories - Category row management utilities.
 *
 * Provides utilities for managing category selection rows in transaction forms,
 * including add/remove rows, category filtering, and date-based category availability.
 *
 * @description This hook provides:
 * - Dynamic category row management (add/remove rows)
 * - Category filtering by transaction date (respects category start dates)
 * - Duplicate category prevention
 * - User category filtering for expenses
 * - Separate handlers for add form and edit form
 *
 * Key concepts:
 * - Categories have start dates - only show categories that existed on transaction date
 * - Prevent selecting same category twice in one transaction
 * - Expenses exclude user categories from category selection
 * - Symmetric operations for add form and edit form
 *
 * @module hooks/useTransactionCategories
 */
import { useCallback } from 'react';

/**
 * Hook for managing transaction category utilities.
 *
 * @param {Array<Object>} activeCategoryObjects - Full category objects with dates and attributes
 * @param {Array<string>} allCategoryNames - All active category names
 * @param {Array<string>} userCategories - User category names
 * @returns {Object} Category utilities
 * @returns {Function} return.getActiveCategoriesForDate - Get categories active for a date
 * @returns {Function} return.addCategoryRow - Add row to add form
 * @returns {Function} return.removeCategoryRow - Remove row from add form
 * @returns {Function} return.updateTransactionCategory - Update category in add form
 * @returns {Function} return.addEditCategoryRow - Add row to edit form
 * @returns {Function} return.removeEditCategoryRow - Remove row from edit form
 * @returns {Function} return.updateEditTransactionCategory - Update category in edit form
 * @returns {Function} return.getAvailableCategories - Get available categories for dropdown
 * @returns {Function} return.getAvailableCategoriesForExpenses - Get categories with allowExpense=true (with grandfathering)
 * @returns {Function} return.getAvailableCategoriesForTransfers - Get categories with allowTransfer=true (with grandfathering)
 * @returns {Function} return.isGrandfatheredCategory - Check if category is grandfathered in edit mode
 *
 * @example
 * const catUtils = useTransactionCategories(categories, categoryNames, userCategories);
 * const availableForDate = catUtils.getActiveCategoriesForDate('2025-10-15');
 * catUtils.addCategoryRow(transactionCategories, setTransactionCategories);
 * const isGrandfathered = catUtils.isGrandfatheredCategory('Food', 'Expense', '2025-10-15');
 */
export const useTransactionCategories = (
    activeCategoryObjects,
    allCategoryNames,
    userCategories
) => {
    // Get active categories for a specific date
    const getActiveCategoriesForDate = useCallback((transactionDate) => {
        if (!transactionDate || !activeCategoryObjects || activeCategoryObjects.length === 0) {
            return allCategoryNames;
        }

        const txDate = new Date(transactionDate);

        // Filter to only show categories that had started by the transaction date
        return activeCategoryObjects.filter(cat => {
            const startDate = new Date(cat.startDate);
            return txDate >= startDate;
        }).map(cat => cat.categoryName);
    }, [activeCategoryObjects, allCategoryNames]);

    // Category row management for add form
    const addCategoryRow = (transactionCategories, setTransactionCategories) => {
        setTransactionCategories([...transactionCategories, { category: '', amount: '' }]);
    };

    const removeCategoryRow = (index, transactionCategories, setTransactionCategories) => {
        if (transactionCategories.length > 1) {
            const newCategories = transactionCategories.filter((_, i) => i !== index);
            setTransactionCategories(newCategories);
        }
    };

    const updateTransactionCategory = (index, field, value, transactionCategories, setTransactionCategories, totalAmount = null) => {
        const newCategories = transactionCategories.map((cat, i) =>
            i === index ? { ...cat, [field]: value } : cat
        );

        // Auto-adjust first category amount if updating amounts of other categories
        if (field === 'amount' && index !== 0 && totalAmount !== null && newCategories.length > 1) {
            const sumOfOtherCategories = newCategories
                .slice(1) // Skip first category
                .reduce((sum, cat) => sum + (parseFloat(cat.amount) || 0), 0);

            const firstCategoryAmount = parseFloat(totalAmount) - sumOfOtherCategories;
            newCategories[0] = {
                ...newCategories[0],
                amount: firstCategoryAmount >= 0 ? firstCategoryAmount.toFixed(2) : '0.00'
            };
        }

        setTransactionCategories(newCategories);
    };

    // Category row management for edit form
    const addEditCategoryRow = (editTransactionCategories, setEditTransactionCategories) => {
        setEditTransactionCategories([...editTransactionCategories, { category: '', amount: '' }]);
    };

    const removeEditCategoryRow = (index, editTransactionCategories, setEditTransactionCategories) => {
        if (editTransactionCategories.length > 1) {
            const newCategories = editTransactionCategories.filter((_, i) => i !== index);
            setEditTransactionCategories(newCategories);
        }
    };

    const updateEditTransactionCategory = (
        index,
        field,
        value,
        editTransactionCategories,
        setEditTransactionCategories,
        totalAmount = null
    ) => {
        const newCategories = editTransactionCategories.map((cat, i) =>
            i === index ? { ...cat, [field]: value } : cat
        );

        // Auto-adjust first category amount if updating amounts of other categories
        if (field === 'amount' && index !== 0 && totalAmount !== null && newCategories.length > 1) {
            const sumOfOtherCategories = newCategories
                .slice(1) // Skip first category
                .reduce((sum, cat) => sum + (parseFloat(cat.amount) || 0), 0);

            const firstCategoryAmount = parseFloat(totalAmount) - sumOfOtherCategories;
            newCategories[0] = {
                ...newCategories[0],
                amount: firstCategoryAmount >= 0 ? firstCategoryAmount.toFixed(2) : '0.00'
            };
        }

        setEditTransactionCategories(newCategories);
    };

    // Get available categories (excluding already selected ones, filtered by date)
    const getAvailableCategories = (options) => {
        const {
            currentIndex,
            isEdit,
            transactionCategories,
            editTransactionCategories,
            date,
            editDate
        } = options;

        const categoryArray = isEdit ? editTransactionCategories : transactionCategories;
        const transactionDate = isEdit ? editDate : date;

        // Get categories active for this transaction date
        const activeCategoryNames = getActiveCategoriesForDate(transactionDate);

        const selectedCategories = categoryArray
            .map((cat, i) => i !== currentIndex ? cat.category : null)
            .filter(cat => cat && cat !== '');

        return activeCategoryNames.filter(cat => !selectedCategories.includes(cat));
    };

    // Get available categories excluding user categories for expenses
    // Also filters to only include categories with allowExpense = true
    // Implements grandfathering: in edit mode, includes categories used in this transaction
    const getAvailableCategoriesForExpenses = (options) => {
        const {
            currentIndex,
            isEdit,
            transactionCategories,
            editTransactionCategories,
            date,
            editDate,
            initialEditCategories
        } = options;

        const categoryArray = isEdit ? editTransactionCategories : transactionCategories;
        const transactionDate = isEdit ? editDate : date;

        const txDate = new Date(transactionDate);

        // Get active categories that:
        // 1. Started by transaction date
        // 2. Are not user categories
        // 3. Have allowExpense = true
        const activeCategoryNames = activeCategoryObjects
            .filter(cat => {
                const startDate = new Date(cat.startDate);
                return txDate >= startDate &&
                       !userCategories.includes(cat.categoryName) &&
                       cat.allowExpense === true;
            })
            .map(cat => cat.categoryName);

        // In edit mode, add grandfathered categories (categories from INITIAL state when edit started)
        let availableCategories = activeCategoryNames;
        if (isEdit && initialEditCategories) {
            const grandfatheredCategories = initialEditCategories
                .filter(cat => cat && cat !== '' && !activeCategoryNames.includes(cat));

            // Combine active and grandfathered, removing duplicates
            availableCategories = [...new Set([...activeCategoryNames, ...grandfatheredCategories])];
        }

        const selectedCategories = categoryArray
            .map((cat, i) => i !== currentIndex ? cat.category : null)
            .filter(cat => cat && cat !== '');

        return availableCategories.filter(cat => !selectedCategories.includes(cat));
    };

    // Get available categories for transfer transactions
    // Filters to only include categories with allowTransfer = true
    // Implements grandfathering: in edit mode, includes categories used in this transaction
    const getAvailableCategoriesForTransfers = (options) => {
        const {
            currentIndex,
            isEdit,
            transactionCategories,
            editTransactionCategories,
            date,
            editDate,
            initialEditCategories
        } = options;

        const categoryArray = isEdit ? editTransactionCategories : transactionCategories;
        const transactionDate = isEdit ? editDate : date;

        const txDate = new Date(transactionDate);

        // Get active categories that:
        // 1. Started by transaction date
        // 2. Have allowTransfer = true
        const activeCategoryNames = activeCategoryObjects
            .filter(cat => {
                const startDate = new Date(cat.startDate);
                return txDate >= startDate && cat.allowTransfer === true;
            })
            .map(cat => cat.categoryName);

        // In edit mode, add grandfathered categories (categories from INITIAL state when edit started)
        let availableCategories = activeCategoryNames;
        if (isEdit && initialEditCategories) {
            const grandfatheredCategories = initialEditCategories
                .filter(cat => cat && cat !== '' && !activeCategoryNames.includes(cat));

            // Combine active and grandfathered, removing duplicates
            availableCategories = [...new Set([...activeCategoryNames, ...grandfatheredCategories])];
        }

        const selectedCategories = categoryArray
            .map((cat, i) => i !== currentIndex ? cat.category : null)
            .filter(cat => cat && cat !== '');

        return availableCategories.filter(cat => !selectedCategories.includes(cat));
    };

    // Check if a category is grandfathered (used in transaction but doesn't meet current criteria)
    const isGrandfatheredCategory = (categoryName, transactionType, transactionDate) => {
        const txDate = new Date(transactionDate);
        const category = activeCategoryObjects.find(cat => cat.categoryName === categoryName);

        if (!category) return false;

        // Check if category started by transaction date
        const startDate = new Date(category.startDate);
        if (txDate < startDate) return false;

        // Check transaction-type-specific criteria
        if (transactionType === 'Expense') {
            // Grandfathered if it's not currently allowed for expenses or is a user category
            return category.allowExpense !== true || userCategories.includes(categoryName);
        } else if (transactionType === 'Transfer') {
            // Grandfathered if it's not currently allowed for transfers
            return category.allowTransfer !== true;
        }

        // Income and Adjustment don't have filtering, so no grandfathering
        return false;
    };

    return {
        getActiveCategoriesForDate,
        addCategoryRow,
        removeCategoryRow,
        updateTransactionCategory,
        addEditCategoryRow,
        removeEditCategoryRow,
        updateEditTransactionCategory,
        getAvailableCategories,
        getAvailableCategoriesForExpenses,
        getAvailableCategoriesForTransfers,
        isGrandfatheredCategory
    };
};
